import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Table, Icon, Tooltip, Select } from "antd";
import CardBlock from "./card-block";
import CallTranscriptDrawer from "./call-transcript-drawer";
import { fetchCallLogs } from "services/telephony-service";

const { Option } = Select;

const DASH = "—";

// Call-status filter options. "" = All (no status param sent).
const STATUS_OPTIONS = [
    { value: "Answered", label: "Answered" },
    { value: "NotAnswered", label: "Not Answered" },
    { value: "Missed", label: "Missed" },
    { value: "Busy", label: "Busy" },
    { value: "Failed", label: "Failed" },
    { value: "", label: "All" }
];

// First non-empty value among the candidates (CDR field names vary by vendor).
const pick = (...vals) => {
    for (let i = 0; i < vals.length; i += 1) {
        const v = vals[i];
        if (v !== undefined && v !== null && v !== "") {
            return v;
        }
    }
    return null;
};

const fmtDateTime = raw => {
    if (!raw) {
        return DASH;
    }
    const m = moment(raw);
    return m.isValid() ? m.format("DD MMM YYYY, hh:mm A") : String(raw);
};

const fmtDuration = raw => {
    if (raw === null || raw === undefined || raw === "") {
        return DASH;
    }
    const secs = Number(raw);
    if (!isFinite(secs) || Number.isNaN(secs)) {
        return String(raw);
    }
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return m ? `${m}m ${s}s` : `${s}s`;
};

// Column config mapped to the Chitthi CDR response shape.
const COLUMNS = [
    {
        title: "Date / Time",
        key: "time",
        render: r => fmtDateTime(pick(r.start_time, r.create_date, r.answer_time))
    },
    {
        title: "Type",
        key: "type",
        render: r => pick(r.call_type, r.type) || DASH
    },
    {
        title: "Customer No.",
        key: "customer",
        render: r => pick(r.customer_number, r.caller_id) || DASH
    },
    {
        title: "Agent",
        key: "agent",
        render: r =>
            pick(
                r.metadata && r.metadata.agent_name,
                r.agent_id,
                r.agent_number
            ) || DASH
    },
    {
        title: "Status",
        key: "status",
        render: r => pick(r.status, r.disposition) || DASH
    },
    {
        title: "Duration",
        key: "duration",
        render: r => fmtDuration(r.duration)
    },
    {
        title: "Talk Time",
        key: "talk_time",
        render: r => fmtDuration(r.talk_time)
    },
    {
        title: "Audio",
        key: "audio",
        align: "center",
        render: r => {
            const url = pick(
                r.internal_recording_url,
                r.vendor_recording_url,
                r.recording_url
            );
            if (!url) {
                return DASH;
            }
            return (
                <Tooltip title={"Play recording (opens in new tab)"}>
                    <a
                        href={url}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                        aria-label={"Play recording"}
                    >
                        <Icon
                            type={"play-circle"}
                            theme={"filled"}
                            style={{ fontSize: 16, color: "#2c5fe0" }}
                        />
                    </a>
                </Tooltip>
            );
        }
    }
];

/**
 * Call Logs — telephony CDR for the current case, shown at the end of the
 * Company Detail section. Server-paginated (page/page_size), styled to match the
 * other Credit Checker tables (cc-pf-antd).
 */
class CallLogsCard extends React.Component {
    state = {
        rows: [],
        count: 0,
        page: 1,
        pageSize: 10,
        // Default filter: Answered calls.
        status: "Answered",
        loading: true,
        // Transcript drawer
        transcriptOpen: false,
        transcriptUniqueId: null,
        transcriptSubtitle: null
    };

    componentDidMount() {
        this.fetchPage(1);
    }

    // LMS keys voice analytics by the telephony vendor's call id, which the
    // Chitthi CDR row carries as `vendor_call_id`.
    openTranscript = row =>
        this.setState({
            transcriptOpen: true,
            transcriptUniqueId: row.vendor_call_id,
            transcriptSubtitle: [
                fmtDateTime(pick(row.start_time, row.create_date)),
                pick(row.customer_number, row.caller_id)
            ]
                .filter(v => v && v !== DASH)
                .join(" · ")
        });

    closeTranscript = () => this.setState({ transcriptOpen: false });

    // Appended to COLUMNS at render time (it needs the instance handler).
    transcriptColumn = () => ({
        title: "Transcript",
        key: "transcript",
        align: "center",
        render: r =>
            r.vendor_call_id ? (
                <Tooltip title={"View call summary and transcript"}>
                    <button
                        type={"button"}
                        className={"cc-calllog__tx-btn"}
                        onClick={() => this.openTranscript(r)}
                    >
                        <Icon type={"message"} />
                        View
                    </button>
                </Tooltip>
            ) : (
                DASH
            )
    });

    setStatus = status =>
        this.setState({ status }, () => this.fetchPage(1));

    fetchPage = page => {
        const { caseId } = this.props;
        this.setState({ loading: true });
        fetchCallLogs({
            caseId,
            page,
            pageSize: this.state.pageSize,
            status: this.state.status
        })
            .then(({ data }) => {
                const body = data || {};
                const rows =
                    body.results ||
                    body.data ||
                    (Array.isArray(body) ? body : []);
                const count =
                    body.count != null ? body.count : rows.length;
                this.setState({ rows, count, page, loading: false });
            })
            .catch(() => {
                this.setState({
                    rows: [],
                    count: 0,
                    loading: false
                });
            });
    };

    render() {
        const { rows, count, page, pageSize, loading } = this.state;
        return (
            <CardBlock
                icon={"phone"}
                title={"Call Logs"}
                subtitle={"Telephony call detail records for this case"}
                action={
                    <span className={"cc-pf-unit"}>
                        <span className={"cc-pf-unit__label"}>Status</span>
                        <Select
                            size={"small"}
                            value={this.state.status}
                            onChange={this.setStatus}
                            dropdownMatchSelectWidth={false}
                            style={{ minWidth: 120 }}
                        >
                            {STATUS_OPTIONS.map(o => (
                                <Option key={o.value} value={o.value}>
                                    {o.label}
                                </Option>
                            ))}
                        </Select>
                    </span>
                }
            >
                <Table
                    className={"cc-pf-antd"}
                    rowKey={(r, i) => r.id || r.uuid || r.call_id || i}
                    columns={[...COLUMNS, this.transcriptColumn()]}
                    dataSource={rows}
                    loading={loading}
                    size={"small"}
                    scroll={{ x: true }}
                    pagination={{
                        current: page,
                        pageSize,
                        total: count,
                        onChange: p => this.fetchPage(p),
                        showSizeChanger: false,
                        size: "small"
                    }}
                />
                <CallTranscriptDrawer
                    visible={this.state.transcriptOpen}
                    uniqueId={this.state.transcriptUniqueId}
                    subtitle={this.state.transcriptSubtitle}
                    onClose={this.closeTranscript}
                />
            </CardBlock>
        );
    }
}

CallLogsCard.propTypes = {
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default CallLogsCard;
